import React, { useRef, useState } from "react";

import { UseMutationResult } from "@tanstack/react-query";
import { isEmpty } from "lodash/fp";
import { DateTime } from "luxon";

import IconHeading from "@app/analytics/components/IconHeading";
import KeyQuestionAnswer, {
  AnswerButton,
  TextFieldWrapper,
} from "@app/analytics/components/KeyQuestionAnswer";
import usePulseClips from "@app/analytics/Pulse/hooks/usePulseClips";
import type { PulseClip, PulseTopic } from "@app/analytics/Pulse/types";
import { isProposedAnswer } from "@app/analytics/utils";
import { toast } from "@app/common/hooks/useToasts";
import type {
  ApiKeyQuestionAnswerClip,
  CreateClipOperation,
  KeyQuestionAnswerClipStatus,
  UpdateClipOperation,
} from "@app/common/types";

import AutoAnimateContainer from "@rui/AutoAnimateContainer";
import ExpandableList from "@rui/ExpandableList";
import { Column, Row } from "@rui/Stack";
import TextField from "@rui/TextField";
import TooltipIcon from "@rui/TooltipIcon";

import { QuestionWrapper } from "./styles";
import ListItem from "../ListItem";
import { BulletedList, CountChip, TableSubHeading, Text } from "../styles";

const QuestionWithoutAnswer = ({
  onAddAnswer,
  question,
}: {
  onAddAnswer: UseMutationResult<void, unknown, CreateClipOperation, void>;
  question: PulseClip;
}) => {
  const [answering, setAnswering] = useState(false);
  const [answerText, setAnswerText] = useState("");

  const inputRef = useRef<HTMLInputElement | null>();

  const revealAnswerInput = () => {
    setAnswering(true);
    // short timeout to give time for the input to animate in
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    setAnswerText(event.target.value);
  };

  const handleSaveAnswer = () => {
    onAddAnswer.mutate(
      {
        type: "key_question_answer",
        createTime: DateTime.utc().toString(),
        parentClipId: question.id,
        sessionId: question.sessionId,
        status: "user_accept",
        text: answerText,
      },
      { onSuccess: () => setAnswering(false) }
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSaveAnswer();
    }
  };

  const answerIsSaving = Boolean(answerText) && onAddAnswer.isPending;

  return (
    <>
      <QuestionWrapper withHoverState={!answering}>
        <ListItem textPrefix="Question: " item={question} />
        {!answering && (
          <AnswerButton
            className="key-question__add-answer"
            onClick={revealAnswerInput}
          >
            Add answer
          </AnswerButton>
        )}
      </QuestionWrapper>
      <Column marginLeft={3}>
        <AutoAnimateContainer show={answering}>
          <TextFieldWrapper marginRight="50px">
            <TextField
              fullWidth
              disabled={answerIsSaving}
              placeholder="Add answer here..."
              ref={inputRef}
              onChange={handleInputChange}
              value={answerText}
              onKeyDown={handleKeyDown}
            />
            <AnswerButton loading={answerIsSaving} onClick={handleSaveAnswer}>
              Add
            </AnswerButton>
          </TextFieldWrapper>
        </AutoAnimateContainer>
      </Column>
    </>
  );
};

const QuestionWithAcceptedAnswer = ({
  answer,
  canEdit,
  question,
  onSaveAnswer,
}: {
  answer: ApiKeyQuestionAnswerClip;
  canEdit: boolean;
  question: PulseClip;
  onSaveAnswer: UseMutationResult<void, unknown, UpdateClipOperation, void>;
}) => {
  const [editing, setEditing] = useState(false);

  const handleSave = (updatedText: string) => {
    setEditing(false);
    onSaveAnswer.mutate(
      {
        id: answer.id,
        type: answer.clipType,
        parentClipId: answer.parentClipId,
        sessionId: answer.sessionId,
        text: updatedText,
      },
      { onError: () => setEditing(true) }
    );
  };

  return (
    <Column>
      <ListItem textPrefix="Question: " item={question} />
      <Column marginBottom={0.5} marginLeft={3}>
        <KeyQuestionAnswer
          answer={answer.text}
          status={answer.status}
          isEditing={editing}
          isSaving={onSaveAnswer.isPending}
          onClickEdit={() => setEditing(true)}
          onSaveAnswer={canEdit ? handleSave : undefined}
        />
      </Column>
    </Column>
  );
};

const QuestionWithProposedAnswer = ({
  answer,
  canEdit,
  onUpdateAnswer,
  question,
}: {
  answer: ApiKeyQuestionAnswerClip;
  canEdit: boolean;
  onUpdateAnswer: UseMutationResult<void, unknown, UpdateClipOperation, void>;
  question: PulseClip;
}) => {
  // use internal state to prevent contamination from updates to other answers
  const [pendingStatus, setPendingStatus] =
    useState<KeyQuestionAnswerClipStatus | null>(null);

  const updateAnswerStatus = (status: KeyQuestionAnswerClipStatus) => {
    setPendingStatus(status);
    onUpdateAnswer.mutate(
      {
        id: answer.id,
        type: answer.clipType,
        parentClipId: answer.parentClipId,
        sessionId: answer.sessionId,
        status,
      },
      {
        onError: () => {
          toast.error(
            "Something went wrong and your update could not be saved. Please refresh the page or try again later."
          );
        },
        onSettled: () => setPendingStatus(null),
      }
    );
  };

  return (
    <Column>
      <ListItem textPrefix="Question: " item={question} />
      <Column marginBottom={0.5} marginLeft={3}>
        <KeyQuestionAnswer
          answer={answer.text}
          onUpdateStatus={canEdit ? updateAnswerStatus : undefined}
          isUpdating={
            pendingStatus === "user_accept" && onUpdateAnswer.isPending
          }
          status={answer.status}
        />
      </Column>
    </Column>
  );
};

const KeyQuestion = ({
  answer,
  canEdit,
  onCreateAnswer,
  onUpdateAnswer,
  question,
}: {
  answer?: ApiKeyQuestionAnswerClip;
  canEdit: boolean;
  onCreateAnswer: UseMutationResult<void, unknown, CreateClipOperation, void>;
  onUpdateAnswer: UseMutationResult<void, unknown, UpdateClipOperation, void>;
  question: PulseClip;
}) => {
  if (answer) {
    if (answer.status === "user_accept") {
      return (
        <QuestionWithAcceptedAnswer
          answer={answer}
          canEdit={canEdit}
          question={question}
          onSaveAnswer={onUpdateAnswer}
        />
      );
    }

    if (isProposedAnswer(answer.status)) {
      return (
        <QuestionWithProposedAnswer
          answer={answer}
          canEdit={canEdit}
          onUpdateAnswer={onUpdateAnswer}
          question={question}
        />
      );
    }
  }

  if (canEdit) {
    return (
      <QuestionWithoutAnswer question={question} onAddAnswer={onCreateAnswer} />
    );
  }

  return <ListItem textPrefix="Question: " item={question} />;
};

const KeyQuestions = ({
  keyQuestions,
}: {
  keyQuestions: PulseTopic["keyQuestions"];
}) => {
  const { answers, questions, createAnswer, updateClip } = usePulseClips();

  return (
    <Column sx={{ px: 3 }} spacing={1}>
      <TableSubHeading>
        <Row spacing={1} variant="left">
          <IconHeading type="key_questions" shape="square" size="small">
            Key Questions & Answers
          </IconHeading>
          <TooltipIcon
            title="Read proposes answers to key questions based on meeting content, when available"
            placement="bottom"
            variant="help"
          />
          {!isEmpty(keyQuestions) && (
            <CountChip>{keyQuestions.length}</CountChip>
          )}
        </Row>
      </TableSubHeading>
      <Column spacing={1}>
        {isEmpty(keyQuestions) ? (
          <Text light>No key questions</Text>
        ) : (
          <BulletedList>
            <ExpandableList max={3} buttonProps={{ sx: { ml: 3.5 } }}>
              {keyQuestions.map((kq) => {
                const answer = answers[kq.id];
                const canEdit = Boolean(
                  answer ? answer.canEdit : questions[kq.id]?.canEdit
                );

                return (
                  <KeyQuestion
                    key={kq.id}
                    answer={answer}
                    canEdit={canEdit}
                    onCreateAnswer={createAnswer}
                    onUpdateAnswer={updateClip}
                    question={kq}
                  />
                );
              })}
            </ExpandableList>
          </BulletedList>
        )}
      </Column>
    </Column>
  );
};

export default KeyQuestions;
